/**
 * @file hooks/useHabitStreak.js
 * @description Manages local-only habit streak tracking based on daily logins.
 */

import { useState, useEffect } from "react";

export const STREAK_KEY = "habit_tracker_data";
const MILESTONE_DISMISSED_KEY = "habit_milestone_dismissed";

const MILESTONES = [
  { days: 7, title: "1 Week Strong!", message: "You've been consistent for 7 days straight. Keep it up!", icon: "🔥" },
  { days: 14, title: "2 Weeks Champion!", message: "14 days of dedication. You're building a real habit!", icon: "💪" },
  { days: 21, title: "3 Weeks Legend!", message: "21 days — they say it takes this long to form a habit. You did it!", icon: "🏆" },
  { days: 28, title: "4 Weeks Unstoppable!", message: "A full month of consistency. You're unstoppable!", icon: "⭐" },
];

export function useHabitStreak() {
  const [streak, setStreak] = useState(0);
  const [milestone, setMilestone] = useState(null);

  useEffect(() => {
    // 1. Get current date keys
    const now = new Date();
    const todayKey = now.toISOString().split("T")[0];

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = yesterday.toISOString().split("T")[0];

    // 2. Retrieve existing data
    const savedData = localStorage.getItem(STREAK_KEY);
    const localData = savedData
      ? JSON.parse(savedData)
      : { last_login: "", habits: 0 };

    // 3. Logic: If already logged in today, just update state and exit
    if (localData.last_login === todayKey) {
      setStreak(localData.habits);
      return;
    }

    // 4. Logic: Check if we continue the streak or reset
    let newStreak = 1;
    if (localData.last_login === yesterdayKey) {
      newStreak = localData.habits + 1;
    }

    // 5. Update LocalStorage & State
    const newData = {
      last_login: todayKey,
      habits: newStreak,
    };

    localStorage.setItem(STREAK_KEY, JSON.stringify(newData));
    setStreak(newStreak);

    // 6. Check for milestone (every 7 days)
    if (newStreak > 0 && newStreak % 7 === 0) {
      const dismissedKey = localStorage.getItem(MILESTONE_DISMISSED_KEY);
      if (dismissedKey !== `${newStreak}-${todayKey}`) {
        const match = MILESTONES.find((m) => m.days === newStreak) ||
          { days: newStreak, title: `${newStreak} Days!`, message: `${newStreak} days of tracking — incredible discipline!`, icon: "🎉" };
        setMilestone(match);
      }
    }
  }, []);

  function dismissMilestone() {
    if (milestone) {
      const todayKey = new Date().toISOString().split("T")[0];
      localStorage.setItem(MILESTONE_DISMISSED_KEY, `${milestone.days}-${todayKey}`);
      setMilestone(null);
    }
  }

  return { streak, milestone, dismissMilestone };
}
